// Twilio-style Number Router for DNI
// Fallback when CallRail is not available or for advanced control

import { DNI_CONFIG, DNI_SESSION_KEY, DNI_CONTEXT_KEY, type DNIContext } from './config';

/**
 * Parse URL parameters
 */
export const getURLParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

/**
 * Detect city from URL path
 * Matches /ppc/tx/{city}/, /tx/{city}/, /ca/{city}/, /ppc/ca/{city}/
 */
export const detectCityFromPath = (path: string): string | null => {
  // PPC Texas city routes: /ppc/tx/{city}/
  const ppcTxMatch = path.match(/^\/ppc\/tx\/([a-z-]+)\/?$/);
  if (ppcTxMatch) return ppcTxMatch[1];
  
  // SEO Texas city routes: /tx/{city}/
  const seoTxMatch = path.match(/^\/tx\/([a-z-]+)\/?$/);
  if (seoTxMatch) return seoTxMatch[1];
  
  // PPC California city routes: /ppc/ca/{city}/
  const ppcCaMatch = path.match(/^\/ppc\/ca\/([a-z-]+)\/?$/);
  if (ppcCaMatch) return ppcCaMatch[1];
  
  // SEO California city routes: /ca/{city}/
  const seoCaMatch = path.match(/^\/ca\/([a-z-]+)\/?$/);
  if (seoCaMatch) return seoCaMatch[1];
  
  // California showroom
  if (path.includes('/showrooms/inland-empire')) return 'inland-empire';
  
  return null;
};

/**
 * Detect if the current path is a California page
 */
export const isCaliforniaPath = (path: string): boolean => {
  const normalizedPath = path.toLowerCase();
  return (
    normalizedPath.startsWith('/ca/') ||
    normalizedPath.startsWith('/ppc/ca/') ||
    normalizedPath.includes('/showrooms/inland-empire')
  );
};

/**
 * Detect if the current path is a Houston page
 * Only /tx/houston/ and /ppc/tx/houston/ should use Houston number
 */
export const isHoustonPath = (path: string): boolean => {
  const normalizedPath = path.toLowerCase();
  return (
    normalizedPath === '/tx/houston/' ||
    normalizedPath === '/tx/houston' ||
    normalizedPath === '/ppc/tx/houston/' ||
    normalizedPath === '/ppc/tx/houston'
  );
};

/**
 * Detect if the current path is an Austin page
 * Only /tx/austin/ and /ppc/tx/austin/ should use Austin number
 */
export const isAustinPath = (path: string): boolean => {
  const normalizedPath = path.toLowerCase();
  return (
    normalizedPath === '/tx/austin/' ||
    normalizedPath === '/tx/austin' ||
    normalizedPath === '/ppc/tx/austin/' ||
    normalizedPath === '/ppc/tx/austin'
  );
};

/**
 * Detect if the current path is a San Antonio page
 * Only /tx/san-antonio/ and /ppc/tx/san-antonio/ should use San Antonio number
 */
export const isSanAntonioPath = (path: string): boolean => {
  const normalizedPath = path.toLowerCase();
  return (
    normalizedPath === '/tx/san-antonio/' ||
    normalizedPath === '/tx/san-antonio' ||
    normalizedPath === '/ppc/tx/san-antonio/' ||
    normalizedPath === '/ppc/tx/san-antonio'
  );
};

/**
 * Get the default phone number based on the current path
 * Priority: Austin > San Antonio > Houston > California > Texas default
 */
export const getDefaultPhoneForPath = (path: string): string => {
  if (isAustinPath(path)) return DNI_CONFIG.DEFAULT_PHONE_AUSTIN;
  if (isSanAntonioPath(path)) return DNI_CONFIG.DEFAULT_PHONE_SAN_ANTONIO;
  if (isHoustonPath(path)) return DNI_CONFIG.DEFAULT_PHONE_HOUSTON;
  if (isCaliforniaPath(path)) return DNI_CONFIG.DEFAULT_PHONE_CA;
  return DNI_CONFIG.DEFAULT_PHONE;
};

/**
 * Determine the tracking number based on priority:
 * 1. UTM parameters (campaign/source)
 * 2. gclid (Google Ads)
 * 3. City from path
 * 4. Default fallback (location-aware)
 */
export const determineTrackingNumber = (): DNIContext => {
  const params = getURLParams();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  
  const utm_campaign = params.utm_campaign || null;
  const utm_source = params.utm_source || null;
  const utm_medium = params.utm_medium || null;
  const gclid = params.gclid || null;
  
  const detectedCity = detectCityFromPath(path);
  const isCalifornia = isCaliforniaPath(path);
  const isHouston = isHoustonPath(path);
  const isAustin = isAustinPath(path);
  const isSanAntonio = isSanAntonioPath(path);
  
  // Use location-appropriate default
  const defaultPhone = getDefaultPhoneForPath(path);
  let assignedNumber = defaultPhone;
  let matchReason = isAustin ? 'default:austin' : 
                    isSanAntonio ? 'default:san-antonio' :
                    isHouston ? 'default:houston' : 
                    (isCalifornia ? 'default:california' : 'default:texas');
  
  // Priority 1: UTM Campaign
  if (utm_campaign && DNI_CONFIG.CAMPAIGN_POOLS[utm_campaign]) {
    assignedNumber = DNI_CONFIG.CAMPAIGN_POOLS[utm_campaign];
    matchReason = `campaign:${utm_campaign}`;
  }
  // Priority 2: UTM Source
  else if (utm_source && DNI_CONFIG.SOURCE_POOLS[utm_source]) {
    assignedNumber = DNI_CONFIG.SOURCE_POOLS[utm_source];
    matchReason = `source:${utm_source}`;
  }
  // Priority 3: gclid present (Google Ads)
  else if (gclid) {
    assignedNumber = DNI_CONFIG.GCLID_POOL;
    matchReason = 'gclid';
  }
  // Priority 4: City from path
  else if (detectedCity && DNI_CONFIG.CITY_POOLS[detectedCity]) {
    assignedNumber = DNI_CONFIG.CITY_POOLS[detectedCity];
    matchReason = `city:${detectedCity}`;
  }
  
  const context: DNIContext = {
    assignedNumber,
    city: detectedCity,
    campaign: utm_campaign,
    source: utm_source,
    medium: utm_medium,
    gclid,
    landingPage: path,
    timestamp: Date.now(),
  };
  
  console.log(`[DNI Router] Assigned ${assignedNumber} (${matchReason})`);
  
  return context;
};

/**
 * Save DNI context to session storage for persistence
 */
export const saveDNIContext = (context: DNIContext): void => {
  if (typeof sessionStorage === 'undefined') return;
  
  try {
    sessionStorage.setItem(DNI_SESSION_KEY, context.assignedNumber);
    sessionStorage.setItem(DNI_CONTEXT_KEY, JSON.stringify(context));
  } catch (e) {
    console.warn('[DNI] Failed to save to sessionStorage', e);
  }
};

/**
 * Load DNI context from session storage
 */
export const loadDNIContext = (): DNIContext | null => {
  if (typeof sessionStorage === 'undefined') return null;
  
  try {
    const stored = sessionStorage.getItem(DNI_CONTEXT_KEY);
    if (stored) {
      return JSON.parse(stored) as DNIContext;
    }
  } catch (e) {
    console.warn('[DNI] Failed to load from sessionStorage', e);
  }
  return null;
};

/**
 * Format phone number for display
 */
export const formatPhoneDisplay = (phone: string, format: 'formatted' | 'raw' = 'raw'): string => {
  const digits = phone.replace(/\D/g, '');
  
  if (format === 'formatted' && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  return phone;
};

/**
 * Get tel: link format
 */
export const getTelLink = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  return `tel:+1${digits}`;
};

/**
 * Swap all phone numbers in the DOM
 */
export const swapPhoneNumbers = (context: DNIContext): void => {
  if (typeof document === 'undefined') return;
  
  const targets = document.querySelectorAll(DNI_CONFIG.CALLRAIL.SWAP_SELECTOR);
  const newNumber = context.assignedNumber;
  const formattedNumber = formatPhoneDisplay(newNumber, 'raw');
  const telLink = getTelLink(newNumber);
  
  targets.forEach((el) => {
    // Update href if it's a link
    if (el instanceof HTMLAnchorElement && el.href.startsWith('tel:')) {
      el.href = telLink;
    }
    
    // Update text content if it looks like a phone number
    const text = el.textContent || '';
    if (/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(text.replace(/[()]/g, ''))) {
      // Preserve parentheses format if original had them
      if (text.includes('(')) {
        el.textContent = formatPhoneDisplay(newNumber, 'formatted');
      } else {
        el.textContent = formattedNumber;
      }
    }
  });
  
  console.log(`[DNI Router] Swapped ${targets.length} phone elements to ${formattedNumber}`);
};

/**
 * Push DNI event to dataLayer
 */
export const pushDNIEvent = (context: DNIContext): void => {
  if (typeof window === 'undefined') return;
  
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: 'phone_number_assigned',
    assigned_number: context.assignedNumber,
    city_detected: context.city,
    utm_source: context.source,
    utm_medium: context.medium,
    utm_campaign: context.campaign,
    gclid: context.gclid,
    landing_page: context.landingPage,
  });
};

/**
 * Initialize the DNI router
 * Should be called on page load
 */
export const initDNIRouter = (): DNIContext => {
  // Check if we have a stored context from this session
  const stored = loadDNIContext();

  // If stored context exists and is from same landing page, use it
  // Otherwise, determine new context (allows for different pages to have different cities)
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  // Guard against legacy/stale stored numbers (e.g. old placeholder CA numbers or old TX numbers)
  const isCA = isCaliforniaPath(currentPath);
  const isHouston = isHoustonPath(currentPath);
  const defaultForPath = getDefaultPhoneForPath(currentPath);
  
  // Check for legacy placeholder CA numbers
  const isLegacyPlaceholderCA =
    isCA &&
    !!stored?.assignedNumber &&
    /^951-555-\d{4}$/.test(stored.assignedNumber.replace(/\D/g, '').replace(/^951555/, '951-555-'));
  
  // Check for legacy TX number (469-404-0899) that was replaced with 469-589-5357
  const isLegacyTXNumber = 
    !isCA &&
    !isHouston &&
    !!stored?.assignedNumber &&
    stored.assignedNumber.replace(/\D/g, '') === '4694040899';
  
  // Check for wrong number on Houston pages (should be 713-730-2453)
  const isWrongHoustonNumber =
    isHouston &&
    !!stored?.assignedNumber &&
    stored.assignedNumber.replace(/\D/g, '') !== '7137302453';

  let context: DNIContext;

  if (stored && stored.landingPage === currentPath && !isLegacyPlaceholderCA && !isLegacyTXNumber && !isWrongHoustonNumber) {
    context = stored;
    console.log('[DNI Router] Using stored context');
  } else {
    context = determineTrackingNumber();

    // If a CA page somehow computed an empty/invalid number, fall back to the CA default
    if (isCA && !context.assignedNumber) {
      context = { ...context, assignedNumber: defaultForPath };
    }

    saveDNIContext(context);
  }

  // Only swap if using Twilio router (CallRail handles its own swapping)
  if (!DNI_CONFIG.USE_CALLRAIL && DNI_CONFIG.USE_TWILIO_ROUTER) {
    // Delay swap to ensure DOM is ready
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => swapPhoneNumbers(context));
    }
  }
  
  // Always push the event
  pushDNIEvent(context);
  
  return context;
};
